/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import SuccessMessage from './components/Message'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  Button,
  useToast
} from '@chakra-ui/react'
import DefaultAlert from '@/components/alerts/DefaultAlert'
import { setErrorCodeAndMessage } from '@/utils/fetch'
import { buildSams } from '@/services/pending-sams'
import { getSuccessDescription } from '@/configs/strings/batches'
import { defaultToastConfig } from '@/configs/toast'
import { useNavigate } from 'react-router-dom'
import { CREATE_SAM_BUTTON_LABEL, CREATE_SAM_MODAL_MESSAGE, CREATE_SAM_MODAL_TITLE } from '@/configs/strings/sams'

const BuildSamModal = ({
  aiMetadataIdsSelected,
  closeModal,
  isProcessing,
  setIsProcessing
}) => {
  const [processEnded, setProcessEnded] = useState(false)
  const [hasAlert, setHasAlert] = useState(false)
  const [alert, setAlert] = useState()
  const [success, setSuccess] = useState(false)
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const toast = useToast()
  const navigate = useNavigate()
  const batchType = 'Build SAM'

  const createLicensedAssets = async () => {
    try {
      setHasAlert(false)
      setAlert(null)
      setIsProcessing(true)

      const sam = await buildSams(aiMetadataIdsSelected, getAccessToken)

      if (sam) {
        toast({
          title: batchType,
          description: getSuccessDescription(
            batchType,
            ''),
          status: 'success',
          ...defaultToastConfig
        })
      }
      navigate('/', { replace: true })

      setProcessEnded(true)
      setIsProcessing(false)
      setSuccess(true)
    } catch (error) {
      setHasAlert(true)
      setAlert(setErrorCodeAndMessage(error))
      setIsProcessing(false)
    }
  }

  const onCloseModal = async () => {
    await closeModal(success)
  }

  return (
    <Modal
      isOpen={true}
      onClose={onCloseModal}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          { CREATE_SAM_MODAL_TITLE }
        </ModalHeader>
        <ModalCloseButton />
        { !processEnded
          ? <>
              <ModalBody>
                { hasAlert &&
                  <DefaultAlert
                    message={alert?.code}
                    status="error"
                  />
                }
                <p>{ CREATE_SAM_MODAL_MESSAGE }</p>
              </ModalBody>
              <ModalFooter>
                <Button
                  data-testid="modal-close-button"
                  variant="ghost"
                  className="mr-sm"
                  onClick={() => onCloseModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={createLicensedAssets}
                  isLoading={isProcessing}
                >
                  { CREATE_SAM_BUTTON_LABEL }
                </Button>
              </ModalFooter>
            </>
          : <ModalBody>
              <SuccessMessage />
            </ModalBody>
        }
      </ModalContent>
    </Modal>
  )
}

export default BuildSamModal
